import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { ExitQuestionPage, ExitQuestionService } from 'src/app/shared/services/exit-question.service';
import { mixpanelTrack } from 'src/app/shared/utils/mixpanel.util';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';

/**
 * Pages a member goes to as the next part of the same flow, not away from it: signing in or up,
 * and the KYC check the loan form sends them to. Leaving for one of these never asks.
 */
const FLOW_CONTINUATION_PATHS = ['/sign-in', '/sign-up', '/kyc', '/kyc-verify', '/kyc-complete'];

const pathOf = (url: string): string => url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';

/**
 * Asks the exit question when a member leaves an unfinished flow: deposit, KYC, loan request,
 * refinance, counter offer, loan resale, resale offer or sale listing.
 *
 * The page says which step to ask about through `exitQuestionStep()`, and returns null once
 * there is nothing to ask: after a submit, while a payment or request is running, in edit mode.
 * The navigation waits for the popup to close and then always goes ahead, so the guard can delay
 * leaving but never block it.
 *
 * Not asked during a sign-out (log out, wallet disconnect, session expiry): the member did not
 * choose to leave, so the drop-off is only counted, as "Exit Question Skipped". Nor when signed
 * out (sign-out clears the stored user before it navigates), when the
 * next page continues the same flow, when another dialog is open (the member is in the middle of
 * an action).
 *
 * Only in-app navigation reaches a guard. Closing the tab or typing a new address cannot show a
 * custom popup. A reload while the popup is open shows it again (see ExitQuestionService).
 */
export const exitQuestionGuard: CanDeactivateFn<ExitQuestionPage> = async (page, _route, currentState, nextState) => {
    const exitQuestion = inject(ExitQuestionService);
    const storage = inject(WebStorageService);

    try {
        if (exitQuestion.consumeSkipNextLeave()) return true;
        const step = page?.exitQuestionStep?.();
        if (!step) return true;
        if (exitQuestion.isSigningOut()) {
            mixpanelTrack('Exit Question Skipped', { step, reason: 'signed_out' });
            return true;
        }
        if (!exitQuestion.canAsk()) return true;
        if (!storage.getLocalStorage('user')) return true;

        const nextPath = pathOf(nextState?.url ?? '');
        if (nextPath === pathOf(currentState.url)) return true;
        if (FLOW_CONTINUATION_PATHS.some((path) => nextPath === path || nextPath.startsWith(`${path}/`))) return true;
        if (document.body.classList.contains('modal-open')) return true;

        await exitQuestion.ask(step, nextState?.url ?? '');
    } catch {
        // no-op: the question must never stop the member leaving.
    }
    return true;
};
